// Owner-scoped server-sent events bridged into the query cache.
//
// Events never carry authority on their own terms: pushed readings are placed
// in cached pages only where they fit (see apply.ts), and everything else is
// an invalidation followed by an HTTP refetch. Reconnecting invalidates the
// whole account, because events sent while disconnected are not replayed, so
// a missed event is never a correctness problem. The connection is closed on
// logout and impersonation changes so one account's stream never feeds
// another account's cache.
//
// While the stream is open, polling backs off (query/refresh.ts); when it
// drops, the data on screen is refetched and polls again until it is back.

import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { parseJson } from '@/lib/json';
import { setStreamLive } from '@/query/liveStream';
import { applyEvent } from './apply';

/**
 * Named events the backend sends. `EventSource` cannot subscribe to arbitrary
 * names, so each is wired explicitly; an unnamed `message` is applied as an
 * unknown event, which invalidates the account.
 */
export const KNOWN_EVENTS = [
  'configuration.desired',
  'readings.new',
  'device.status',
  'device.capabilities',
  'device.event',
  'source.rejection',
  'group.changed',
] as const;

function invalidateAccountData(queryClient: QueryClient, accountId: string): void {
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'account' && query.queryKey[1] === accountId,
  });
}

/** The event's JSON payload, with large integers kept exact. */
function payload(event: MessageEvent<string>): Record<string, unknown> {
  try {
    const parsed = parseJson(event.data);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function useEventStream(accountId: string | null, enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !accountId) return;
    if (typeof EventSource === 'undefined') return;

    const source = new EventSource('/api/events', { withCredentials: true });

    // On (re)connect, refetch everything scoped to this account: events sent
    // while disconnected were never received and cannot be replayed.
    const onOpen = (): void => {
      setStreamLive(true);
      invalidateAccountData(queryClient, accountId);
    };
    // The browser retries on its own. Until it is back, data on screen must
    // poll again, so refetch it now: each refetch picks the faster interval.
    const onError = (): void => {
      if (setStreamLive(false)) invalidateAccountData(queryClient, accountId);
    };
    const onMessage = (event: MessageEvent<string>): void =>
      applyEvent(queryClient, accountId, 'message', payload(event));
    const named = KNOWN_EVENTS.map((name) => {
      const listener = (event: MessageEvent<string>): void =>
        applyEvent(queryClient, accountId, name, payload(event));
      source.addEventListener(name, listener);
      return [name, listener] as const;
    });

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener('message', onMessage);

    return () => {
      source.removeEventListener('open', onOpen);
      source.removeEventListener('error', onError);
      source.removeEventListener('message', onMessage);
      for (const [name, listener] of named) source.removeEventListener(name, listener);
      source.close();
      setStreamLive(false);
    };
  }, [accountId, enabled, queryClient]);
}
